import { stripAttachmentDownloadLinks } from "./attachments"
import { HASHTAG_REGEX } from "./hashtags"
import { stripHighlights } from "./highlights"
import { explodeTableCells } from "./index"
import { legacyTiptapTitle, legacyTiptapToMarkdown } from "./legacyTiptap"
import { stripMath } from "./math"

// Block-level prefixes.
// A bare marker ("#", what an empty heading line trims to) is a prefix too, as in Swift and Kotlin.
const HEADING_PREFIX_RE = /^#{1,6}(?:\s+|$)/
const BLOCKQUOTE_PREFIX_RE = /^\s*(?:>\s*)+/
const TASK_LIST_PREFIX_RE = /^\s*[-*+]\s+\[( |x|X)\]\s+/
const UNORDERED_LIST_PREFIX_RE = /^\s*[-*+]\s+/
const ORDERED_LIST_PREFIX_RE = /^\s*\d+[.)]\s+/
// Inline syntax.
const IMAGE_RE = /!\[[^\]]*\]\([^)]*\)/g
const HTML_BR_RE = /<br\s*\/?>/gi
const NOTE_MENTION_RE = /\[\[(?:[^|\]]+\|)?([^\]]+)\]\]/g
const INLINE_CODE_RE = /`([^`]+)`/g
const BOLD_RE = /\*\*([^*]+)\*\*/g
const BOLD_ALT_RE = /__([^_]+)__/g
const ITALIC_RE = /\*([^*]+)\*/g
const ITALIC_ALT_RE = /_([^_]+)_/g
const STRIKETHROUGH_RE = /~~([^~]+)~~/g
const LINK_RE = /\[([^\]]+)\]\([^)]+\)/g
// CommonMark backslash escapes: a `\` before ASCII punctuation is syntax, and
// the punctuation is what the reader sees. `## 1\. Capture` is the common one
// (escaping the dot so the line is not read as an ordered list), and without
// this the backslash leaks into every plain-text rendering of that heading or
// title. Runs LAST, after the block-prefix strips: unescaping `1\.` into `1. `
// any earlier would make ORDERED_LIST_PREFIX_RE eat the number.
// Mirrored by `markdownEscapePattern` in Swift's MarkdownStripper.swift.
//
// Exported for `stripInlineFormatting` (the preview pipeline in `index.ts`),
// which has the same defect and the same ordering constraint. The regex itself
// is not re-exported from the barrel — it is internal to this package, and
// outside callers use `unescapeMarkdown` below. One regex, three callers.
export const MARKDOWN_ESCAPE_RE = /\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g

/**
 * Undo CommonMark backslash escapes so plain-text output reads the way the
 * rendered document does: `1\.` becomes `1.`, `C\+\+` becomes `C++`, `\\`
 * becomes a single `\`. A `\` before anything that is not ASCII punctuation is
 * literal text and is left alone.
 *
 * **Run it LAST**, after every block-prefix strip. Unescaping earlier turns
 * `1\. Preflight` into `1. Preflight`, which an ordered-list rule then eats
 * down to `Preflight` — and likewise `\#`, `\-`, `\>` into real heading,
 * bullet and quote markers. It also must run exactly once per string: a second
 * pass eats the surviving `\` that an escaped backslash (`\\`) produced.
 *
 * **Only for plain-text output.** Anything destined for a markdown renderer
 * (`renderInlineMarkdown`, `AttributedString(markdown:)`, react-markdown) must
 * stay escaped — those parsers unescape themselves, and pre-unescaping makes
 * `\*x\*` render as real emphasis.
 *
 * Mirrors `unescapeMarkdown` in Swift's CaliuCore `MarkdownStripper.swift`.
 */
export function unescapeMarkdown(text: string): string {
  return text.replace(MARKDOWN_ESCAPE_RE, "$1")
}

/**
 * Normalize a single line (or a stored note title) to plain readable text:
 * strips block-level prefixes (heading / quote / list / task markers) plus
 * inline markdown (bold, italic, strikethrough, code, links, note mentions,
 * highlights, images, file-attachment links, `<br>`, backslash escapes).
 *
 * Mirrors Swift's `plainTextTitle` (CaliuCore `MarkdownStripper.swift`) so a
 * markdown title authored on one client renders as clean text on every other.
 * Change both or neither.
 *
 * ponytail: Swift's and Kotlin's table-pipe stripping is not mirrored here:
 * every title writer explodes table cells first (`extractNoteTitle`), so a
 * title is one cell, never a `| a | b |` row. A heading whose text holds pipes
 * (`# A | B`) keeps them here and loses them natively. Mirror the stripping
 * (with table-line detection, so prose with a literal `|` survives) if that
 * ever matters.
 */
export function plainTextTitle(raw: string): string {
  if (!raw) return ""

  let result = raw.trim()
  if (!result) return ""

  // A heading's text is inline content: nothing block-level follows `# `
  // ("# 2026. 10. 20." is a heading titled "2026. 10. 20.", not a list), so
  // once a heading marker is stripped no other block prefix is. Quote, list
  // and task markers are stripped only from a line that is not a heading.
  const afterHeading = result.replace(HEADING_PREFIX_RE, "")
  if (afterHeading !== result) {
    result = afterHeading
  } else {
    result = result.replace(BLOCKQUOTE_PREFIX_RE, "")
    if (TASK_LIST_PREFIX_RE.test(result)) {
      result = result.replace(TASK_LIST_PREFIX_RE, "")
    } else {
      result = result
        .replace(UNORDERED_LIST_PREFIX_RE, "")
        .replace(ORDERED_LIST_PREFIX_RE, "")
    }
  }

  return plainInlineText(result)
}

/**
 * A stored note title as plain text. The title was already read off its line
 * by `extractNoteTitle` (on any client or the worker), so its block prefix is
 * gone: stripping one again would eat real text, like the day of the German
 * date title "20. Oktober 2026" or the number of "1. Intro". Only a heading
 * marker is stripped (older worker titles could keep a bare `#`), then inline
 * markdown.
 *
 * Mirrors Swift and Kotlin `storedTitleText`. Change all three or none.
 */
export function storedTitleText(raw: string): string {
  const result = raw.trim().replace(HEADING_PREFIX_RE, "")
  return result ? plainInlineText(result) : ""
}

/**
 * A note's title: the line `titleSourceLine` picks, as plain text, or `""`
 * when no line has text. Legacy TipTap JSON reads its first node with text.
 * The one extractor every writer of `notes.title` uses: the web autosave and
 * the worker here, Swift and Kotlin `extractNoteTitle` (same scan).
 */
export function extractNoteTitle(content: string): string {
  if (!content) return ""
  const body = skipTitleFrontmatter(content)
  const legacy = legacyTiptapTitle(body)
  if (legacy !== null) return legacy
  const source = titleSourceLine(body)
  return source === null ? "" : plainTextTitle(source.line)
}

// The inline half of `plainTextTitle`: images and attachment links dropped,
// inline markdown stripped, escapes undone (last, once).
function plainInlineText(text: string): string {
  // Drop images and file-attachment links entirely (they render as
  // thumbnails / chips, never as title text).
  const result = stripAttachmentDownloadLinks(stripMath(stripHighlights(text.replace(IMAGE_RE, ""))))

  return result
    .replace(HTML_BR_RE, " ")
    .replace(NOTE_MENTION_RE, "$1")
    .replace(INLINE_CODE_RE, "$1")
    .replace(BOLD_RE, "$1")
    .replace(BOLD_ALT_RE, "$1")
    .replace(STRIKETHROUGH_RE, "$1")
    .replace(LINK_RE, "$1")
    .replace(ITALIC_RE, "$1")
    .replace(ITALIC_ALT_RE, "$1")
    .replace(MARKDOWN_ESCAPE_RE, "$1")
    .replace(/\s{2,}/g, " ")
    .trim()
}

// A leading TOML block (Hugo's `+++` fences), after the optional YAML one and
// any blank lines. Closed by a whole `+++` line, or by the end of the note.
// `(?![\s\S])`, not `$`: ICU and Java let `$` match before a final line
// break, JS does not, and the three clients must agree.
const TOML_FRONTMATTER_RE = /^\n*\+\+\+\n(?:[\s\S]*?\n)?\+\+\+(?:\n|(?![\s\S]))/

/**
 * The part of a note its title is read from: the content after a leading YAML
 * (`---`) frontmatter block and then a TOML (`+++`) one, so a Hugo note is not
 * titled "+++". Only titles use it: folds stay YAML-only.
 * Mirrored by Swift `skipTitleFrontmatter` (CaliuCore `MarkdownStripper.swift`)
 * and Kotlin `skipTitleFrontmatter` (`:core`), all three running
 * `titleFrontmatter.fixtures.json`. Change all three or none.
 */
export function skipTitleFrontmatter(content: string): string {
  let body = content
  if (body.startsWith("---\n")) {
    const end = body.indexOf("\n---\n", 4)
    if (end !== -1) body = body.slice(end + 5)
  }
  const toml = TOML_FRONTMATTER_RE.exec(body)
  return toml ? body.slice(toml[0].length) : body
}

// Lines the title scan passes over: a thematic break or a code-fence delimiter.
const TITLE_DECORATOR_RE = /^(?:[-*_]{3,}|`{3,}.*|~{3,}.*)$/
const CODE_FENCE_RE = /^(?:`{3,}|~{3,})/
// An ATX heading line, `#` to `######` then whitespace. A setext heading
// (`Title` over `===`) is not one: the editors only ever write ATX.
const ATX_HEADING_RE = /^#{1,6}\s+/

/**
 * The line a markdown note's title is read from, trimmed, or `null` when no
 * line has title text: after the frontmatter, with table cells exploded, the
 * first line that is not blank, not a decorator and whose `plainTextTitle` is
 * not empty. The same scan as the web autosave's `extractTitleFromContent` and
 * Swift / Kotlin `extractNoteTitle`. `inFence`: a code-fence delimiter came
 * before it, so the line is code (a `# comment` is not a heading).
 */
function titleSourceLine(markdown: string): { line: string; inFence: boolean } | null {
  let inFence = false
  for (const line of explodeTableCells(skipTitleFrontmatter(markdown)).split("\n")) {
    const trimmed = line.trim()
    if (!trimmed) continue
    if (TITLE_DECORATOR_RE.test(trimmed)) {
      if (CODE_FENCE_RE.test(trimmed)) inFence = true
      continue
    }
    if (plainTextTitle(trimmed)) return { line: trimmed, inFence }
  }
  return null
}

/**
 * Whether a note has a clear title: the line its title is read from is an ATX
 * heading. A note that starts with a paragraph, a list, a quote, a table or a
 * fence (whose first line may well be a `# comment`) has a title too, but not
 * a clear one, so a card shows it as body text.
 * An empty `# ` has no text, so it is never the title line. Legacy TipTap JSON
 * is read as the markdown it converts to.
 *
 * Mirrored by Swift `hasHeadingTitle` (CaliuCore `MarkdownStripper.swift`) and
 * Kotlin `hasHeadingTitle` (`:core` `NoteTitle.kt`), all three running
 * `headingTitle.fixtures.json`. Change all three or none.
 */
export function hasHeadingTitle(content: string): boolean {
  const source = titleSourceLine(legacyTiptapToMarkdown(content))
  return source !== null && !source.inFence && ATX_HEADING_RE.test(source.line)
}

// A letter or a digit: what `hidesCardTitle` reads as text once tags are gone.
const READABLE_RE = /[\p{L}\p{N}]/u

/**
 * Whether a note card shows no title row: the note has no clear title
 * (`hasHeadingTitle`) and the line its title is read from has a letter or a
 * digit once hashtags are dropped, so line 1 of the preview stands in for the
 * title. Otherwise (`# \n\n#tag`, `#idea`, a lone `- `, `$$`, a title read
 * from inside a code fence, which previews leave out, empty) the card keeps the
 * title row it has always had. One rule for every density; a card whose kept
 * preview is still empty shows its title row too.
 *
 * Mirrored by Swift `hidesCardTitle` (CaliuCore `MarkdownStripper.swift`) and
 * Kotlin `hidesCardTitle` (`:core` `NoteTitle.kt`), all three running
 * `headingTitle.fixtures.json`. Change all three or none.
 */
export function hidesCardTitle(content: string): boolean {
  const source = titleSourceLine(legacyTiptapToMarkdown(content))
  if (source === null || source.inFence || ATX_HEADING_RE.test(source.line)) return false
  return READABLE_RE.test(plainTextTitle(source.line).replace(HASHTAG_REGEX, " "))
}
